import {
  useAddCreatorsToScriptGenerateRequest,
  useCreateScriptGenerateRequest,
  useGetAiWorkspaceRequest,
  useGetAiWorkspaceRequests,
} from "@entities/aiWorkspace";
import {
  IAIWorkspace,
  IAIWorkspaceRequest,
} from "@entities/aiWorkspace/lib/types";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import { SocialMedia, SocialMediaSearch } from "@shared/lib/types/influencer";
import { useState } from "react";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import { creatorsFromRequest, freshDraft } from "./creatorsFromRequest";
import { downloadScriptPdf } from "./downloadScriptPdf";
import { ScriptGenerationEditor } from "./ScriptGenerationEditor";
import { ScriptGenerationList } from "./ScriptGenerationList";
import {
  creatorKey,
  readScriptGenerationMasterPrompt,
  ScriptDraft,
  ScriptPlatform,
} from "./types";
import { useSingleScriptGenerateRequest } from "./useSingleScriptGenerateRequest";

type ScriptEdit = Partial<Pick<ScriptDraft, "brief" | "settings" | "markdown" | "status">>;

function openedScriptRequest(
  detail: IAIWorkspaceRequest | undefined,
  requests: IAIWorkspaceRequest[] | undefined,
  requestId?: string
) {
  if (!requestId) return undefined;
  const fromDetail = detail?.type === "script-generate" ? detail : undefined;
  const fromList = requests?.find(
    (item) =>
      item.type === "script-generate" && (item._id === requestId || item.id === requestId)
  );
  if (fromDetail?.creatorScriptGenerations?.length) return fromDetail;
  return fromList || fromDetail;
}

type Incoming = Partial<SocialMedia> & Partial<SocialMediaSearch>;

function toCreator(item: Incoming) {
  const platform = String(item.platform || "").toLowerCase();
  const username = String(item.username || "")
    .trim()
    .replace(/^@+/, "");
  if ((platform !== "instagram" && platform !== "youtube") || !username)
    return null;
  return { platform: platform as ScriptPlatform, username };
}

export function ScriptGenerationTab({
  workspace,
  requestId,
  creatorScriptGeneratorJobId,
  onOpenRequest,
  onSelectCreatorJob,
  onGeneratingChange,
}: {
  workspace: IAIWorkspace;
  requestId?: string;
  creatorScriptGeneratorJobId?: string;
  onOpenRequest?: (id: string, type: string) => void;
  onSelectCreatorJob?: (jobId: string) => void;
  onGeneratingChange?: (generating: boolean) => void;
}) {
  const overviewBrief = promptToText(workspace.overview || "");
  const masterPrompt = readScriptGenerationMasterPrompt(
    workspace.scriptGenerationMasterPrompt
  );
  const { activeId, isResolving } = useSingleScriptGenerateRequest(
    workspace._id,
    requestId,
    onOpenRequest
  );
  const [edits, setEdits] = useState<Record<string, ScriptEdit>>({});
  const [editRequestId, setEditRequestId] = useState(requestId);
  if (editRequestId !== requestId) {
    setEditRequestId(requestId);
    setEdits({});
  }

  const { data: requestData, isLoading } = useGetAiWorkspaceRequest(activeId);
  const { data: allRequests } = useGetAiWorkspaceRequests(workspace._id);
  const request = openedScriptRequest(
    requestData?.data,
    allRequests?.data,
    activeId
  );
  const creators = creatorsFromRequest(request);
  const drafts = creators.map((creator) => ({
    ...freshDraft(creator, overviewBrief, masterPrompt),
    ...edits[creator.id],
    id: creator.id,
    creator,
  }));

  const { create, isCreating } = useCreateScriptGenerateRequest(
    (res: BackednResponse<IAIWorkspaceRequest>) => {
      const newId = res?.data?._id || res?.data?.id;
      if (newId) onOpenRequest?.(newId, "script-generation");
    }
  );
  const { addCreators: addToRequest, isAdding } =
    useAddCreatorsToScriptGenerateRequest();

  const addCreator = (incoming: Incoming) => {
    const nextCreators = [incoming]
      .map(toCreator)
      .filter(
        (creator): creator is { platform: ScriptPlatform; username: string } =>
          Boolean(creator)
      );
    if (!nextCreators.length) {
      ToastService.error("Username or platform is missing.");
      return;
    }
    const existing = new Set(creators.map((creator) => creator.id));
    const fresh = nextCreators.filter(
      (creator) => !existing.has(creatorKey(creator.platform, creator.username))
    );
    if (!fresh.length) {
      ToastService.error("Those creators are already in this request.");
      return;
    }
    const payload = fresh.map((creator) => ({
      username: creator.username,
      platform: creator.platform,
    }));
    if (activeId) {
      addToRequest({ aiWorkspaceRequestId: activeId, creators: payload });
      return;
    }
    create({ aiWorkspaceId: workspace._id, creators: payload });
  };

  const active =
    drafts.find(
      (draft) => draft.creator.jobId === creatorScriptGeneratorJobId
    ) || null;

  const openCreator = (id: string) => {
    const draft = drafts.find((item) => item.id === id);
    if (draft?.creator.jobId) onSelectCreatorJob?.(draft.creator.jobId);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const download = async () => {
    if (!active?.markdown.trim() || isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadScriptPdf({
        username: active.creator.username,
        markdown: active.markdown,
      });
      ToastService.success("Script PDF downloaded");
    } catch {
      ToastService.error("Failed to generate PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  if (active) {
    return (
      <ScriptGenerationEditor
        active={active}
        aiWorkspaceId={workspace._id}
        onBack={() => onSelectCreatorJob?.("")}
        onChange={(patch) =>
          setEdits((current) => ({
            ...current,
            [active.id]: { ...current[active.id], ...patch },
          }))
        }
        onDownload={download}
        isDownloading={isDownloading}
        onGeneratingChange={onGeneratingChange}
      />
    );
  }

  return (
    <ScriptGenerationList
      creators={drafts.map((draft) => draft.creator)}
      onAdd={addCreator}
      onOpen={openCreator}
      isBusy={isCreating || isAdding || isResolving || isLoading}
    />
  );
}
